import React from 'react';
import { css } from '@emotion/react';
import { ModalCloseBtn } from 'public/assets';
import SPLoading from '@components/commons/loading/SPLoading';
import { useOnboardingPopupContext } from '@provider/OnboardingPopupProvider';

import ChoicePopup from './ChoicePopup';
import { NamePopup, TextPopup } from './Popups';
import ProfileUpdateList from './ProfileUpdateList';
import ProfileImagePopup from './ProfileImagePopup';
import ChooseProfile from './ChooseProfile';
import theme from '@/styles/theme';
import { Story_Play_Type } from '@/baseType';
import { StoryGameTextPopup } from './StoryGameTextPopup';
import HomeIcon from './HomeIcon';
import StoryGameAiImageUpload from './StoryGameAiImageUpload';
import StoryGameAiImageSelectPopup from './StoryGameAiImageSelectPopup';
import StoryGameAiImageFilterPopup from './StoryGameAiImageFilterPopup';
import StoryGameImageProfileCropPopup from './StoryGameImageProfileCropPopup';
import StoryGameAiImagePreview from './StoryGameAiImagePreview';
import StoryGameOnboardingAlert from '@/components/storyGame/popup/StoryGameOnboardingAlert';
import StoryGameToast from '@/components/commons/popup/StoryGameToast';
import MobileStoryGameAiImageSelectPopup from './MobileStoryGameAiImageSelectPopup';
import { useWindowWidth } from '@/hooks/useWindowWidth';
import MobileStoryGameAiImagePreview from './MobileStoryGameAiImagePreview';
import { isMobileDevice } from '@/common/utils';

// const aiCharactersEnabled = true;

interface IOnBoardingPopup {
  hasPadding?: boolean;
  onClose: () => void;
}

const OnBoardingPopup = ({ hasPadding, onClose }: IOnBoardingPopup) => {
  const {
    playStory,
    onboardings,
    onboarding,
    profileType,
    aiProfileType,
    isLoading,
    onboardingIndex,
    choiceProfilesRef,
    currentNameRef,
    onShowProfile,
    onCloseSelectImage,
    onSelectImage,
    onConfirmChooseProfile,
    onNextPopup,
    onUpdateProfileImage,
    setName,
    onNextChoice,
    onCloseUpdateProfile,
    setAiProfileType,
    onChangeAiProfile,
    aiProfileInfo,
    onCreateAiCharacter,
    onGetAiCharacterSessionId,
    aiCharacterSessionId,
    onGetAiCharacterPromptOptions,
    aiCharacterPromptOptions,
    onGetAiCharacterImageList,
    aiCharacterImageList,

    onClickStoryGameCharacter,
    selectedCharacter,
    onCreateCharacter,
    aiProfileInfoArray,
    isShowPromotionStorygame,
    addOpenedStorygameIds,
    setIsShowPromotionStorygame,
    storygamePopupBg,
    isStoryGameEdit,
    setIsStoryGameEdit,
    selectPreviousCharacter,
    selectNextCharacter,
    storygameAlertData,
    setStorygameAlertData,
    isNameMissingForSave,
    onClickHomeIcon,
    onClickMyImage,
    storygameToastData,
    setStorygameToastData,
    removeSessionId,
  } = useOnboardingPopupContext();
  const { isPC } = useWindowWidth();
  const isMobile = isMobileDevice();
  if (!onboarding || !playStory) {
    return null;
  }

  const onClosePopup = () => {
    profileType ? onCloseUpdateProfile() : onClose();
  };

  // 스겜에 자캐생성 팝업. or 스겜 온보딩이 전체화면으로 바꾸면 여기 온보딩타입 추가되어야함.
  const isStoryGameFullModal =
    playStory.playType === Story_Play_Type.Storygame &&
    (onboarding.type === 'StorygameProfile' || onboarding.type === 'Text');
  return (
    <div
      css={Container({
        isStoryGameFullModal,
        bg: storygamePopupBg,
        hasPadding,
        isMobile,
      })}
    >
      <div className="bg" />

      <div className="content">
        {isStoryGameFullModal ? (
          <div
            className={'top-left'}
            onClick={() => {
              onClickHomeIcon();
            }}
          >
            {!isPC && aiProfileType ? null : <HomeIcon />}
          </div>
        ) : (
          <div className="close">
            <ModalCloseBtn width={24} height={24} onClick={onClosePopup} />
          </div>
        )}

        {profileType === 'UpdateProfile' ? (
          <ProfileUpdateList onSelect={onShowProfile} />
        ) : profileType === 'ImageUpload' ? (
          <ProfileImagePopup
            onSelectCancel={() => {
              onCloseSelectImage(undefined);
            }}
            onSelectConfirm={(customProfile?: {
              file: File;
              fileUrl: string;
            }) => {
              onCloseSelectImage(customProfile);
            }}
          />
        ) : profileType === 'ChooseProfile' ? (
          <ChooseProfile
            onBoarding={onboarding}
            onSelectMyImage={onSelectImage}
            onConfirm={onConfirmChooseProfile}
            customImage={choiceProfilesRef.current.find(
              (item) => item.popupId === onboarding.popupId,
            )}
          />
        ) : onboarding.type === 'Text' ? (
          playStory.playType === Story_Play_Type.Storygame ? ( // 테스트 를 위해 Text에 하드코딩. 나중에 AiCharacterImage로 옮겨야함.
            <StoryGameTextPopup
              isLastPopup={onboardingIndex.current >= onboardings.length - 1}
              description={onboarding.description}
              onNext={onNextPopup}
            />
          ) : (
            <TextPopup
              isLastPopup={onboardingIndex.current >= onboardings.length - 1}
              description={onboarding.description}
              onNext={onNextPopup}
            />
          )
        ) : onboarding.type === 'Name' || onboarding.type === 'OtherName' ? (
          <NamePopup
            isLastPopup={onboardingIndex.current >= onboardings.length - 1}
            playStory={playStory}
            onBoarding={onboarding}
            onName={setName}
            onUpdateProfileImage={onUpdateProfileImage}
            choiceProfiles={choiceProfilesRef.current}
            currentName={currentNameRef.current}
          />
        ) : onboarding.type === 'Choice' ||
          onboarding.type === 'Profile' ||
          onboarding.type === 'OtherProfile' ? (
          <ChoicePopup
            onBoarding={onboarding}
            isLastPopup={onboardingIndex.current >= onboardings.length - 1}
            onNext={onNextChoice}
          />
        ) : onboarding.type === 'StorygameProfile' && aiCharacterSessionId ? (
          <>
            {isStoryGameEdit ? (
              isPC ? (
                <StoryGameAiImageSelectPopup
                  // isLastPopup={onboardingIndex.current >= onboardings.length - 1}
                  // description={onboarding.description}
                  bg={storygamePopupBg}
                  setAiProfileType={setAiProfileType}
                  onNext={onNextPopup}
                  onChangeAiProfile={onChangeAiProfile}
                  onGetAiCharacterSessionId={onGetAiCharacterSessionId}
                  onGetAiCharacterImageList={onGetAiCharacterImageList}
                  aiCharacterImageList={aiCharacterImageList}
                  onClickStoryGameCharacter={onClickStoryGameCharacter}
                  selectedCharacter={selectedCharacter}
                  onCreateCharacter={onCreateCharacter}
                  aiProfileInfo={aiProfileInfo}
                  aiProfileInfoArray={aiProfileInfoArray}
                  isShowPromotionStorygame={isShowPromotionStorygame}
                  addOpenedStorygameIds={addOpenedStorygameIds}
                  setIsShowPromotionStorygame={setIsShowPromotionStorygame}
                  selectPreviousCharacter={selectPreviousCharacter}
                  selectNextCharacter={selectNextCharacter}
                  storygameAlertData={storygameAlertData}
                  setStorygameAlertData={setStorygameAlertData}
                  isNameMissingForSave={isNameMissingForSave}
                  onClickMyImage={onClickMyImage}
                />
              ) : (
                <MobileStoryGameAiImageSelectPopup
                  // isLastPopup={onboardingIndex.current >= onboardings.length - 1}
                  // description={onboarding.description}
                  bg={storygamePopupBg}
                  setAiProfileType={setAiProfileType}
                  onNext={onNextPopup}
                  onChangeAiProfile={onChangeAiProfile}
                  onGetAiCharacterSessionId={onGetAiCharacterSessionId}
                  onGetAiCharacterImageList={onGetAiCharacterImageList}
                  aiCharacterImageList={aiCharacterImageList}
                  onClickStoryGameCharacter={onClickStoryGameCharacter}
                  selectedCharacter={selectedCharacter}
                  onCreateCharacter={onCreateCharacter}
                  aiProfileInfo={aiProfileInfo}
                  aiProfileInfoArray={aiProfileInfoArray}
                  isShowPromotionStorygame={isShowPromotionStorygame}
                  addOpenedStorygameIds={addOpenedStorygameIds}
                  setIsShowPromotionStorygame={setIsShowPromotionStorygame}
                  selectPreviousCharacter={selectPreviousCharacter}
                  selectNextCharacter={selectNextCharacter}
                  storygameAlertData={storygameAlertData}
                  setStorygameAlertData={setStorygameAlertData}
                  isNameMissingForSave={isNameMissingForSave}
                  onClickMyImage={onClickMyImage}
                />
              )
            ) : isPC ? (
              <StoryGameAiImagePreview
                bg={storygamePopupBg}
                setAiProfileType={setAiProfileType}
                onNext={onNextPopup}
                onChangeAiProfile={onChangeAiProfile}
                onGetAiCharacterSessionId={onGetAiCharacterSessionId}
                onGetAiCharacterImageList={onGetAiCharacterImageList}
                aiCharacterImageList={aiCharacterImageList}
                onClickStoryGameCharacter={onClickStoryGameCharacter}
                selectedCharacter={selectedCharacter}
                onCreateCharacter={onCreateCharacter}
                aiProfileInfo={aiProfileInfo}
                aiProfileInfoArray={aiProfileInfoArray}
                isShowPromotionStorygame={isShowPromotionStorygame}
                addOpenedStorygameIds={addOpenedStorygameIds}
                setIsShowPromotionStorygame={setIsShowPromotionStorygame}
                setIsStoryGameEdit={setIsStoryGameEdit}
                selectPreviousCharacter={selectPreviousCharacter}
                selectNextCharacter={selectNextCharacter}
                storygameAlertData={storygameAlertData}
                setStorygameAlertData={setStorygameAlertData}
                removeSessionId={removeSessionId}
              />
            ) : (
              <MobileStoryGameAiImagePreview
                bg={storygamePopupBg}
                setAiProfileType={setAiProfileType}
                onNext={onNextPopup}
                onChangeAiProfile={onChangeAiProfile}
                onGetAiCharacterSessionId={onGetAiCharacterSessionId}
                onGetAiCharacterImageList={onGetAiCharacterImageList}
                aiCharacterImageList={aiCharacterImageList}
                onClickStoryGameCharacter={onClickStoryGameCharacter}
                selectedCharacter={selectedCharacter}
                onCreateCharacter={onCreateCharacter}
                aiProfileInfo={aiProfileInfo}
                aiProfileInfoArray={aiProfileInfoArray}
                isShowPromotionStorygame={isShowPromotionStorygame}
                addOpenedStorygameIds={addOpenedStorygameIds}
                setIsShowPromotionStorygame={setIsShowPromotionStorygame}
                setIsStoryGameEdit={setIsStoryGameEdit}
                selectPreviousCharacter={selectPreviousCharacter}
                selectNextCharacter={selectNextCharacter}
                storygameAlertData={storygameAlertData}
                setStorygameAlertData={setStorygameAlertData}
                setStorygameToastData={setStorygameToastData}
                removeSessionId={removeSessionId}
              />
            )}
            {aiProfileType !== undefined && <div className="popup-bg-blur" />}
            <div style={{ position: 'absolute', zIndex: 4 }}>
              {aiProfileType === 'AiImageUpload' ? (
                <StoryGameAiImageUpload
                  aiProfileInfo={aiProfileInfo}
                  setAiProfileType={setAiProfileType}
                  onChangeAiProfile={onChangeAiProfile}
                />
              ) : aiProfileType === 'AiImageFilter' ? (
                <StoryGameAiImageFilterPopup
                  price={onboarding.price}
                  aiProfileInfo={aiProfileInfo}
                  setAiProfileType={setAiProfileType}
                  onChangeAiProfile={onChangeAiProfile}
                  onCreateAiCharacter={async (data) => {
                    const res = await onCreateAiCharacter(data);
                    addOpenedStorygameIds();
                    return res;
                  }}
                  aiCharacterPromptOptions={aiCharacterPromptOptions}
                  onGetAiCharacterPromptOptions={onGetAiCharacterPromptOptions}
                  selectedCharacter={selectedCharacter}
                  setStorygameToastData={setStorygameToastData}
                  setStorygameAlertData={setStorygameAlertData}
                  paymentProduct={onboarding.paymentProduct}
                />
              ) : aiProfileType === 'ProfileCrop' ? (
                <StoryGameImageProfileCropPopup
                  initialImageUrl={aiProfileInfo?.portraitFile?.link}
                  onCrop={function (croppedImage: File): void {
                    onChangeAiProfile({ profileFile: croppedImage });
                  }}
                  onBack={function (): void {
                    setAiProfileType(undefined);
                  }}
                />
              ) : null}
            </div>
          </>
        ) : null}

        {isLoading && <SPLoading />}
        <StoryGameOnboardingAlert storygameAlertData={storygameAlertData} />
        <StoryGameToast
          message={storygameToastData.message}
          onHide={() =>
            setStorygameToastData({ message: '', isVisible: false })
          }
          visible={storygameToastData.isVisible}
        />
      </div>
    </div>
  );
};

const Container = ({
  isStoryGameFullModal,
  bg,
  hasPadding = true,
  isMobile,
}: {
  isStoryGameFullModal: boolean;
  bg?: string;
  hasPadding?: boolean;
  isMobile: boolean;
}) =>
  isStoryGameFullModal
    ? css`
        height: 100%;
        width: 100%;
        min-width: 100%;
        padding: 0;
        overflow: hidden;
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        border: none;
        background-image: url(${bg});
        background-repeat: no-repeat;
        background-position: center;
        background-size: cover;

        .popup-bg-blur {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(0, 0, 0, 0.25);

          z-index: 2;
          backdrop-filter: blur(8px);
        }

        .content {
          width: 100%;
          height: 100%;
          background-color: transparent;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          position: relative;
          border-radius: 0.625rem;
          margin-top: env(safe-area-inset-top);

          .close {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            padding: 0.75rem 0.75rem 0rem 0.75rem;
            top: 0;
            left: 0;
            z-index: 1;
            background-color: 'red';
            cursor: pointer;
          }
        }

        .top-left {
          position: absolute;
          top: 12px;
          left: 12px;
          display: flex;
          align-items: center;
          z-index: 4;
          @media screen and (max-width: ${theme.devices.md}px) {
            left: 8px;
            top: ${isMobile ? '0' : '4'}px;
          }
        }

        @media screen and (max-width: ${theme.devices.md}px) {
          padding: 0 0rem;
        }
      `
    : css`
        height: 100%;
        width: 100%;
        min-width: 26.75rem;
        padding: 0;
        overflow: hidden;
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        border: none;
        border-radius: 1rem;
        background-color: ${theme.colors.black};

        .bg {
          position: fixed;
          top: 0;
          left: 0;
          opacity: 0.6;
          width: 100vw;
          height: 100vh;
          background-color: black;
        }

        .content {
          width: 100%;
          max-width: 26.75rem;
          background-color: white;
          display: flex;
          flex-direction: column;
          justify-content: center;
          position: relative;
          border-radius: 0.625rem;

          .close {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            padding: 0.75rem 0.75rem 0.25rem 0.75rem;
            top: 0;
            left: 0;
            z-index: 1;
            background-color: 'red';
            cursor: pointer;
          }
        }

        @media screen and (max-width: ${theme.devices.mobile}) {
          padding: ${hasPadding ? '0 2rem' : '0'};
          min-width: 100%;
        }
      `;

export default OnBoardingPopup;
